import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

export interface ValidateMessageParams {
  message: string;
}

interface SuccessValidateMessageResponse {
  message: string;
}

export interface ErrorValidateMessageResponse {
  Error: {
    message: string[];
  };
  Data: {
    message: string;
  };
}

export type ValidateMessageResponse =
  | SuccessValidateMessageResponse
  | ErrorValidateMessageResponse;

const _validateMessage = async (
  { message }: ValidateMessageParams,
  signal?: AbortSignal
): Promise<AxiosResponse<ValidateMessageResponse>> => {
  return api.post('v2/p2p/validate_message', { message }, { signal });
};

// Uses useQuery (with the message in the query key) so multiple callers across
// the tree share a single in-flight request and cache repeat validations for
// the same text. Passes React Query's AbortSignal to axios so stale requests
// get cancelled when the user keeps editing — prevents validate_message spam.
export const useValidateMessage = (message: string) => {
  return useQuery({
    queryKey: ['validateMessage', message],
    queryFn: ({ signal }) => _validateMessage({ message }, signal),
    enabled: !!message,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
